import type { SearchParams } from '@/server/searchParams.ts';
import { ADULTS_DEFAULT, URLParameters } from '@/app/common/urlParameters.ts';
import { ampli } from '@/ampli';
import type { SearchInfoData, SearchInfoDetailData } from '@/app/search/types.ts';
import { getTotalNumAdults, getTotalNumChildren, getTotalNumInfants, getTotalNumRooms } from '@/utils/peopleUtils.ts';
import { createStayDates, getDestinationsType, getRoomsDetails } from '@/app/common/starTrack/utils.ts';
import { createDatesParams, createOriginValue } from '@/app/common/memsearch/getSearchParams.ts';
import { createPartialStay } from '@/app/common/starTrack/events/utils/eventsUtils.ts';
import { getSearchModeFromSearchParams } from '@/app/components/search/utils/query2.ts';
import type { CommonData } from '@/app/common/createCommonData.ts';
import { getABTests, getUUID } from '@/app/common/amplitude/events/ampliEventsUtils.ts';
import { canUseAmplitude } from '@/app/common/amplitude/amplitudeUtils.ts';
import type { Localization } from '@/app/components/search/common/Localization';

export type AmpliSearchRequestedProps = {
  searchParams: SearchParams;
  searchInfoData: SearchInfoData;
  searchInfoDetailData: SearchInfoDetailData;
  commonData: CommonData;
  localization: Localization;
};

export const ampliSearchRequested = ({
  searchParams,
  searchInfoData,
  searchInfoDetailData,
  commonData,
  localization,
}: AmpliSearchRequestedProps) => {
  if (!canUseAmplitude(commonData, localization)) return;

  const { searchId, searchType } = searchInfoData;
  const { abTests } = commonData;

  const dateFrom = searchParams[URLParameters.DATE_FROM];
  const dateTo = searchParams[URLParameters.DATE_TO];
  const adddays = searchParams[URLParameters.ADD_DAYS];
  const destination = decodeURIComponent(searchParams?.[URLParameters.DESTINATION] || '').toString();
  const adults = searchParams[URLParameters.ADULTS] || ADULTS_DEFAULT;
  const ages = searchParams[URLParameters.AGES];
  const origin = searchParams[URLParameters.ORIGIN];

  const destinationType = searchInfoDetailData?.destination?.destinationType;
  const datesParams = createDatesParams(adddays ? parseInt(adddays, 10) : 0, dateFrom, dateTo);
  const { datefrom: searchDateFrom, dateto: searchDateTo } = datesParams;
  const stayDates = createStayDates({ dateFrom: searchDateFrom, dateTo: searchDateTo });
  const trackingDestinationType = getDestinationsType(destination, destinationType);
  const partialStay = createPartialStay(searchParams);
  const roomDetails = getRoomsDetails({ ages: (ages && ages.toString()) || '', adults: adults.toString() });
  const searchMode = getSearchModeFromSearchParams(searchParams);
  const searchOrigin = createOriginValue(searchMode, origin);
  const departure = searchOrigin ? searchOrigin.toString() : '';
  const uuid = getUUID(commonData);

  ampli.searchRequested({
    ...getABTests(abTests),
    'adult qty': getTotalNumAdults(searchParams[URLParameters.ADULTS]) || ADULTS_DEFAULT,
    'child qty': getTotalNumChildren(searchParams[URLParameters.AGES]),
    departure,
    'departure date from': stayDates.departureDate.from || '',
    'departure date to': stayDates.departureDate.to || '',
    destination: searchParams[URLParameters.DESTINATION] || 'unknown',
    'destination name': searchInfoDetailData.destination?.description,
    'destination type': trackingDestinationType,
    'features enabled': [], // TODO: Implement in a future iteration
    'infant qty': getTotalNumInfants(searchParams[URLParameters.AGES]),
    'length of stay max': stayDates.lengthOfStay.max || 0,
    'length of stay min': stayDates.lengthOfStay.min || 0,
    'partial stay flag': partialStay.flag,
    'return date from': stayDates.returnDate.from || '',
    'return date to': stayDates.returnDate.to || '',
    'room details': roomDetails,
    'room qty': getTotalNumRooms(searchParams[URLParameters.ADULTS]),
    'search id': searchId,
    'search type': searchType,
    uuid,
  });
};
